import React, { useState } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { formatPrice } from '../utils/formatters';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

interface Location {
  lat: number;
  lng: number;
}

interface Listing {
  id: string;
  title: string;
  price: number;
  displayLocation: string;
  coordinates: Location;
  images: string[];
  beds: number;
  baths: number;
  sqft: number;
}

interface MapProps {
  listings: Listing[];
  onMarkerClick?: (listing: Listing) => void;
  center?: { lat: number; lng: number };
  zoom?: number;
  selectedListing?: string | null;
}

const Map: React.FC<MapProps> = ({ 
  listings, 
  onMarkerClick,
  center = { lat: 40.7128, lng: -74.0060 }, // Default to NYC
  zoom = 13,
  selectedListing
}) => {
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  const mapOptions = {
    disableDefaultUI: false,
    clickableIcons: false,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  };

  const handleMarkerClick = (listing: Listing) => {
    setHoveredMarker(listing.id);
    onMarkerClick?.(listing);
  };

  const handleInfoWindowClose = () => {
    setHoveredMarker(null);
  };

  const createMarkerLabel = (price: number) => ({
    text: formatPrice(price),
    className: 'marker-label',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '14px',
    fontWeight: '600'
  });

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={center}
      zoom={zoom}
      options={mapOptions}
      onClick={() => setHoveredMarker(null)}
    >
      {listings.map((listing) => (
        <React.Fragment key={listing.id}>
          <Marker
            position={listing.coordinates}
            onClick={() => handleMarkerClick(listing)}
            onMouseOver={() => setHoveredMarker(listing.id)}
            label={createMarkerLabel(listing.price)}
            icon={{
              path: 'M0,0z', // Invisible path
              scale: 0, // Makes the default marker invisible
              labelOrigin: new google.maps.Point(0, 0)
            }}
          />

          {hoveredMarker === listing.id && (
            <InfoWindow
              position={listing.coordinates}
              onCloseClick={handleInfoWindowClose}
              options={{
                pixelOffset: new google.maps.Size(0, -20),
                maxWidth: 320
              }}
            >
              <div className="relative">
                <button
                  onClick={handleInfoWindowClose}
                  className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 z-10"
                >
                  <X className="h-4 w-4" />
                </button>
                <Link 
                  to={`/listings/${listing.id}`}
                  className="block hover:opacity-95 transition-opacity"
                >
                  <div className="p-1">
                    <div className="relative aspect-[4/3] w-[300px] mb-3">
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute top-2 right-2 bg-black/75 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {formatPrice(listing.price)}/mo
                      </div>
                    </div>
                    <div className="px-1">
                      <h3 className="font-medium text-gray-900 mb-1 line-clamp-1">
                        {listing.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-2">{listing.displayLocation}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{listing.beds === 0 ? 'Studio' : `${listing.beds} bed${listing.beds > 1 ? 's' : ''}`}</span>
                        <span>•</span>
                        <span>{listing.baths} bath{listing.baths > 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{listing.sqft} sqft</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </InfoWindow>
          )}
        </React.Fragment>
      ))}
    </GoogleMap>
  );
};

export default Map;